/**
 * The three shares of a finished solve (docs/SHARING-BUILD.md):
 *
 * - `challenge` ("Challenge a friend"): this device's challenge for the daily (asked for as
 *   the poster opens, `prepareMyChallenge`), shared as the dare, ending in `/c/:code`;
 * - `result` ("Share result only"): the same text without "Beat me?", linking the daily
 *   (or the practice puzzle);
 * - `rematch` ("Send the rematch", after playing someone's challenge): your own challenge,
 *   shared the same way, and tracked as `rematch_sent`.
 *
 * The text comes from the engine (`buildChallengeShareText` / `buildResultShareText`):
 * the time, Flow, checks, the emoji board and a percentile only when the api has an
 * honest one. The tap never waits on the network for more than about a second (UX-045):
 * iOS only lets a share or a copy happen close to the tap. Without a code by then the dare
 * goes on the daily's own link, and the result says so (`plain`). Never throws.
 */
import {
	buildChallengeShareText,
	buildResultShareText,
	type CellOutcome,
	type DateKey,
	DEFAULT_LEVEL,
	type Level,
} from '@arrow/game-engine';

import { fetchPercentile } from '../api/client.ts';
import type { ShareVariant } from '../platform/analytics.ts';
import { track } from '../platform/analytics.ts';
import { prepareMyChallenge, recordMyChallenge } from '../platform/challenges.ts';
import type { ChallengeContext } from './challenge.ts';
import { challengeUrl, newShareId, type ShareMethod, shareResult, shareUrl } from './share.ts';

/** What a share needs to know about the solve. */
export interface SolveForShare {
	kind: 'daily' | 'practice' | 'puzzle';
	number: number | null;
	level?: Level | undefined;
	puzzleId?: string | undefined;
	elapsedMs: number;
	bestFlow: number;
	checks: number;
	reveals: number;
	outcomes?: readonly CellOutcome[] | undefined;
	width?: number | undefined;
	/** Draw the emoji board (Settings → Picture board in shares). Default true. */
	board?: boolean | undefined;
	/** The challenge this solve was played from (a rematch points back at it). */
	challenge?: ChallengeContext | null | undefined;
}

export interface ShareDeps {
	share: typeof shareResult;
	/** The challenge's code (memoised, so the poster's early ask is usually ready). */
	prepareChallenge: (daily: { number: number; level: Level }) => Promise<string | null>;
	/** Remember and track a challenge once it's shared. */
	recordChallenge: (c: { code: string; number: number; level: Level; durationMs: number; todayKey: DateKey }) => void;
	percentile: (number: number, level: Level, ms: number) => Promise<number | null>;
	track: typeof track;
	shareId: () => string;
}

/** Only ask the api this long for the percentile before sharing without it. */
export const PERCENTILE_WAIT_MS = 1000;
/** …and for the challenge's code, before sharing the dare on the daily's own link. */
export const CHALLENGE_WAIT_MS = 1000;

const percentiles = new Map<string, Promise<number | null>>();

/**
 * The percentile for a time (memoised per page, so the poster can ask when it opens and
 * the share finds it ready). Null until the api has enough solvers, or on any failure.
 */
export function percentileFor(number: number, level: Level, ms: number): Promise<number | null> {
	const key = `${number}:${level}:${Math.round(ms)}`;
	let hit = percentiles.get(key);
	if (!hit) {
		hit = fetchPercentile(number, level, ms).then((s) => s?.percentile ?? null);
		percentiles.set(key, hit);
	}
	return hit;
}

/** Tests: forget memoised percentiles. */
export function resetPercentiles(): void {
	percentiles.clear();
}

const defaultDeps: ShareDeps = {
	share: shareResult,
	prepareChallenge: (daily) => prepareMyChallenge(daily),
	recordChallenge: (c) => recordMyChallenge(c),
	percentile: percentileFor,
	track,
	shareId: newShareId,
};

function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
	return new Promise((resolve) => {
		const id = setTimeout(() => resolve(fallback), ms);
		p.then(
			(v) => {
				clearTimeout(id);
				resolve(v);
			},
			() => {
				clearTimeout(id);
				resolve(fallback);
			},
		);
	});
}

/** The texts, without sending anything (the share sheet's preview and tests). */
export function shareTextFor(
	solve: SolveForShare,
	variant: ShareVariant,
	opts: { code?: string | null; percentile?: number | null } = {},
): string {
	const isDaily = solve.kind === 'daily' && (solve.number ?? 0) > 0;
	const level = solve.level ?? DEFAULT_LEVEL;
	const input = {
		number: isDaily ? solve.number! : 0,
		level,
		elapsedMs: solve.elapsedMs,
		bestFlow: solve.bestFlow,
		checks: solve.checks,
		reveals: solve.reveals,
		...(solve.outcomes && solve.width ? { outcomes: solve.outcomes, width: solve.width } : {}),
		board: solve.board !== false,
		// The percentile is about today's dailies only.
		percentile: isDaily ? (opts.percentile ?? null) : null,
	};
	const dailyLink = shareUrl({
		number: isDaily ? solve.number : null,
		level: solve.level,
		puzzleId: solve.kind === 'practice' ? solve.puzzleId : undefined,
	});
	if (variant === 'result' || !isDaily) return buildResultShareText({ ...input, url: dailyLink });
	// No code (offline, or solve sync is off): still a dare, on the daily's own link.
	return buildChallengeShareText({ ...input, url: opts.code ? challengeUrl(opts.code) : dailyLink });
}

export interface ShareSolveResult {
	method: ShareMethod;
	/** The challenge code shared, when there is one. */
	code: string | null;
	text: string;
	/** A dare or rematch that went without a code (offline, or not ready): no time to beat. */
	plain: boolean;
}

/**
 * Shares a finished solve as `variant` (practice can only share its result). Tracks
 * `share_pressed` with the variant and a fresh share id, and `rematch_sent`.
 */
export async function shareSolve(
	solve: SolveForShare,
	variant: ShareVariant,
	opts: { todayKey: DateKey },
	deps: ShareDeps = defaultDeps,
): Promise<ShareSolveResult> {
	const isDaily = solve.kind === 'daily' && (solve.number ?? 0) > 0;
	const kind = solve.kind === 'daily' ? ('daily' as const) : solve.kind === 'practice' ? ('practice' as const) : undefined;
	const v: ShareVariant = isDaily ? variant : 'result';
	const level = solve.level ?? DEFAULT_LEVEL;
	const shareId = deps.shareId();
	try {
		const percentile = isDaily
			? withTimeout(deps.percentile(solve.number!, level, solve.elapsedMs), PERCENTILE_WAIT_MS, null)
			: Promise.resolve(null);
		const codeAsk =
			v === 'result' ? Promise.resolve(null) : withTimeout(deps.prepareChallenge({ number: solve.number!, level }), CHALLENGE_WAIT_MS, null);
		const [code, pct] = await Promise.all([codeAsk, percentile]);
		const text = shareTextFor(solve, v, { code, percentile: pct });
		const method = await deps.share(text, { number: solve.number, level: solve.level, ...(kind ? { kind } : {}), variant: v, shareId });
		const sent = method !== 'cancelled' && method !== 'failed';
		if (code && sent) deps.recordChallenge({ code, number: solve.number!, level, durationMs: solve.elapsedMs, todayKey: opts.todayKey });
		if (v === 'rematch' && solve.challenge && sent) {
			deps.track('rematch_sent', {
				code: solve.challenge.code,
				number: solve.challenge.number,
				level: solve.challenge.level,
				method,
				rematchCode: code,
			});
		}
		return { method, code, text, plain: v !== 'result' && !code };
	} catch {
		return { method: 'failed', code: null, text: '', plain: false };
	}
}
